import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { OverlayDialog } from "@/shared/components/ui/OverlayDialog";
import { useRouteOverlay } from "@/shared/hooks/useRouteOverlay";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { SearchResults } from "../components/SearchResults";
import { useSearch } from "../hooks/searchQueries";

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const searchPhrase = searchParams.get("q") ?? "";
  const query = searchPhrase.trim();
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const isDebouncing = query !== debouncedQuery;
  const { business, businessId, locationId } = useCurrentBusiness();
  const { closeOverlay } = useRouteOverlay(`/b/${businessId}/l/${locationId}`);
  const search = useSearch(query, !isDebouncing && !!business);
  const validQuery = query.length >= 2 && query.length <= 100;
  const isSearching = validQuery && (isDebouncing || search.isFetching);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(timeout);
  }, [query]);

  function changeSearch(value: string) {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set("q", value);
      else next.delete("q");
      return next;
    }, { replace: true, state: location.state });
  }

  const data = search.data;
  const hasResults = data && (
    data.clients.length > 0 || data.appointments.length > 0 || data.services.length > 0
  );

  return (
    <OverlayDialog title="Search" onClose={closeOverlay} className="h-[calc(100dvh-32px)]" contentClassName="space-y-[26px]">
        <div className="space-y-3">
          <ListSearch
            id="global-search"
            label="Search customers, appointments and services"
            placeholder="Name, email, phone or service"
            value={searchPhrase}
            onChange={changeSearch}
            inputProps={{
              "aria-invalid": query.length > 0 && !validQuery,
              "aria-describedby": query.length > 100 ? "search-help search-validation" : "search-help",
              autoComplete: "off",
            }}
          />
          <p id="search-help" className="text-xs leading-relaxed text-muted-foreground">
            Use 2–100 characters. Customer and service names find appointments;
            email and phone find customers.
          </p>
        </div>
        <div className="min-w-0 space-y-[26px]" aria-live="polite" aria-busy={isSearching} hidden={query.length < 2}>
          {query.length > 100 ? (
            <div id="search-validation">
              <ErrorState
                title="Search is too long"
                message="Use 2 to 100 characters. Your text is kept so you can shorten it."
              />
            </div>
          ) : query.length < 2 ? (
            null
          ) : !business || !businessId ? (
            <ErrorState
              title="Business unavailable"
              message="Choose an available business before searching."
            />
          ) : isSearching ? (
            <LoadingSpinner message="Searching…" />
          ) : search.error ? (
            <ErrorState
              title="Couldn't search"
              message={search.error.message}
              onRetry={() => void search.refetch()}
            />
          ) : search.fetchStatus === "paused" ? (
            <ErrorState
              title="Search is offline"
              message="Check your connection. Search will resume when you're online."
              onRetry={() => void search.refetch()}
            />
          ) : !data ? (
            <LoadingSpinner message="Searching…" />
          ) : !hasResults ? (
            <EmptyState
              title="No matches"
              message="Try another name, email, phone number or service."
            />
          ) : (
            <SearchResults data={data} query={query} />
          )}
        </div>
    </OverlayDialog>
  );
}
